import { z } from 'zod'
import { leaveTypeIdSchema } from './leave.js'

export const dashboardStatsSchema = z.object({
  pending: z.number().int().nonnegative(),
  approved: z.number().int().nonnegative(),
  totalDaysApproved: z.number().nonnegative(),
  onLeaveToday: z.number().int().nonnegative(),
})
export type DashboardStats = z.infer<typeof dashboardStatsSchema>

export const dashboardTeamSchema = z.object({
  id: z.guid(),
  name: z.string(),
  memberCount: z.number().int().nonnegative(),
  slotPct: z.number().int().min(0).max(100),
  /** ceil(memberCount * slotPct/100), min 1. */
  slotsToday: z.number().int().nonnegative(),
  onLeaveToday: z.number().int().nonnegative(),
  pendingCount: z.number().int().nonnegative(),
  /** Working days in the next N days where onLeave >= slots. */
  fullDays: z.object({
    count: z.number().int().nonnegative(),
    /** Up to first 5 dates, ordered ascending. */
    dates: z.array(z.string().date()),
  }),
  /** Working days where onLeave == slots - 1. */
  tightDays: z.number().int().nonnegative(),
})
export type DashboardTeam = z.infer<typeof dashboardTeamSchema>

export const whoIsOutItemSchema = z.object({
  employeeId: z.guid(),
  employeeName: z.string(),
  employeePreferredName: z.string().nullable(),
  teamId: z.guid(),
  teamName: z.string(),
  leaveType: leaveTypeIdSchema,
  startDate: z.string().date(),
  endDate: z.string().date(),
  reason: z.string().nullable().optional(),
})
export type WhoIsOutItem = z.infer<typeof whoIsOutItemSchema>

export const weekTriagePersonSchema = z.object({
  employeeId: z.guid(),
  employeeName: z.string(),
  leaveType: leaveTypeIdSchema,
  status: z.enum(['approved', 'pending']),
})
export type WeekTriagePerson = z.infer<typeof weekTriagePersonSchema>

export const weekTriageDaySchema = z.object({
  date: z.string().date(),
  isWeekend: z.boolean(),
  isHoliday: z.boolean(),
  holidayName: z.string().nullable().optional(),
  slotsTotal: z.number().int().nonnegative(),
  onLeaveApproved: z.number().int().nonnegative(),
  onLeavePending: z.number().int().nonnegative(),
  people: z.array(weekTriagePersonSchema),
})
export type WeekTriageDay = z.infer<typeof weekTriageDaySchema>

export const weekTriageWorstDaySchema = z.object({
  date: z.string().date(),
  headcountIn: z.number().int().nonnegative(),
  ifAllApproved: z.number().int().nonnegative(),
  slotsTotal: z.number().int().nonnegative(),
})
export type WeekTriageWorstDay = z.infer<typeof weekTriageWorstDaySchema>

export const weekTriagePayloadSchema = z.object({
  weekStart: z.string().date(),
  weekEnd: z.string().date(),
  days: z.array(weekTriageDaySchema),
  pendingInWindow: z.array(z.guid()),
  worstDay: weekTriageWorstDaySchema.nullable().optional(),
})
export type WeekTriagePayload = z.infer<typeof weekTriagePayloadSchema>

export const dashboardPayloadSchema = z.object({
  generatedAt: z.string().datetime(),
  horizonDays: z.number().int().positive(),
  stats: dashboardStatsSchema,
  teams: z.array(dashboardTeamSchema),
  whoIsOutThisWeek: z.array(whoIsOutItemSchema),
  weekTriage: weekTriagePayloadSchema.nullable().optional(),
})
export type DashboardPayload = z.infer<typeof dashboardPayloadSchema>

// ── HOD overview ─────────────────────────────────────────────────────────────

export const stressLevelSchema = z.enum(['calm', 'warn', 'critical'])
export type StressLevel = z.infer<typeof stressLevelSchema>

export const pressureWeekSchema = z.object({
  start: z.string().date(),
  outCount: z.number().int().nonnegative(),
  headcount: z.number().int().nonnegative(),
  stress: stressLevelSchema,
})
export type PressureWeek = z.infer<typeof pressureWeekSchema>

export const pressureTeamSchema = z.object({
  teamId: z.guid(),
  teamName: z.string(),
  managerName: z.string().nullable(),
  openEscalations: z.number().int().nonnegative(),
  patternFlags: z.number().int().nonnegative(),
  // Max people who can be out at once — the coverage floor. Lets the UI show
  // "2 / 3 out" instead of a bare % with no reference point.
  capacity: z.number().int().nonnegative(),
  weeks: z.array(pressureWeekSchema),
})
export type PressureTeam = z.infer<typeof pressureTeamSchema>

export const hodEscalationRowSchema = z.object({
  id: z.guid(),
  leaveId: z.guid(),
  employee: z.string(),
  reason: z.string().nullable(),
  openedAt: z.string(),
  // Breadcrumb so the HOD can answer "why is this in front of me and not the
  // manager?" Null for auto-escalated leaves and rows pre-dating enrichment.
  managerName: z.string().nullable().optional(),
  managerNote: z.string().nullable().optional(),
  escalatedAt: z.string().nullable().optional(),
})
export type HodEscalationRow = z.infer<typeof hodEscalationRowSchema>

export const hodUpcomingEventSchema = z.object({
  kind: z.enum(['blackout', 'public-holiday']),
  label: z.string(),
  range: z.string(),
})
export type HodUpcomingEvent = z.infer<typeof hodUpcomingEventSchema>

export const hodOverviewResponseSchema = z.object({
  pressure: z.array(pressureTeamSchema),
  escalations: z.array(hodEscalationRowSchema),
  upcoming: z.array(hodUpcomingEventSchema),
  divisionPatternFlags: z.number().int().nonnegative(),
})
export type HodOverviewResponse = z.infer<typeof hodOverviewResponseSchema>
